import { IconClock, IconMail } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

type NewsletterSignupProps = {
	className?: string;
};

export function NewsletterSignup({ className }: NewsletterSignupProps) {
	const [email, setEmail] = useState("");
	const [lastPostDate, setLastPostDate] = useState<number | null>(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);
	const [submitted, setSubmitted] = useState(false);
	const [submitError, setSubmitError] = useState<string | null>(null);

	useEffect(() => {
		let isMounted = true;
		const load = async () => {
			try {
				setLoading(true);
				const res = await fetch("/api/newsletter");
				if (!res.ok) throw new Error(`Failed to load newsletter info`);
				const data = (await res.json()) as { last_published_at?: number };
				const ts = data.last_published_at;
				if (isMounted) setLastPostDate(ts && ts > 0 ? ts : null);
			} catch (err) {
				console.error(err);
				if (isMounted) setError("Couldn't load newsletter info");
			} finally {
				if (isMounted) setLoading(false);
			}
		};
		load();
		return () => {
			isMounted = false;
		};
	}, []);

	const formattedLastPublished = lastPostDate
		? new Date(lastPostDate * 1000).toLocaleDateString(undefined, {
				year: "numeric",
				month: "long",
				day: "numeric",
			})
		: null;

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		if (!email.trim()) return;

		setSubmitting(true);
		setSubmitError(null);

		try {
			const res = await fetch("/api/newsletter", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({ email: email.trim() }),
			});

			if (!res.ok) {
				const errorData = (await res.json().catch(() => ({}))) as {
					message?: string;
					error?: string;
				};
				const detail =
					(typeof errorData.message === "string" && errorData.message.trim()) ||
					(typeof errorData.error === "string" && errorData.error.trim()) ||
					"Failed to subscribe";
				throw new Error(detail);
			}

			setSubmitted(true);
		} catch (err) {
			console.error("Newsletter subscription error:", err);
			setSubmitError(
				err instanceof Error
					? err.message
					: "Failed to subscribe. Please try again.",
			);
		} finally {
			setSubmitting(false);
		}
	}

	const cardClass = cn(
		"group relative flex h-full min-h-0 w-full min-w-0 flex-col overflow-hidden rounded-xl border border-border/80 bg-card shadow-md transition-shadow duration-300 hover:shadow-lg dark:shadow-none dark:hover:shadow-none",
		className,
	);

	if (submitted) {
		return (
			<Card className={cardClass}>
				<CardHeader className="space-y-1 p-4 pb-2">
					<CardTitle className="flex items-center gap-2 text-lg font-semibold text-foreground">
						<span className="flex size-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
							<IconMail className="size-4" />
						</span>
						Thank you
					</CardTitle>
				</CardHeader>
				<CardContent className="flex flex-1 flex-col space-y-2 p-4 pt-0 text-center sm:text-left">
					<p className="text-pretty text-sm text-muted-foreground">
						You&apos;re subscribed. Check your inbox for a confirmation email.
					</p>
				</CardContent>
			</Card>
		);
	}

	return (
		<Card className={cardClass}>
			<div
				className="pointer-events-none absolute inset-0 bg-linear-to-br from-primary/[0.04] to-transparent"
				aria-hidden
			/>
			<CardHeader className="relative space-y-1 p-4 pb-2">
				<CardTitle className="flex items-center gap-2 text-lg font-semibold text-foreground">
					<span className="flex size-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
						<IconMail className="size-4" />
					</span>
					Newsletter
				</CardTitle>
			</CardHeader>
			<CardContent className="relative flex flex-1 flex-col space-y-3 p-4 pt-0">
				<p className="text-pretty text-base leading-relaxed text-muted-foreground sm:text-sm">
					Occasional notes on deliverability, projects, and what I&apos;m
					learning.
				</p>
				<form className="flex flex-1 flex-col gap-3" onSubmit={handleSubmit}>
					<Input
						type="email"
						placeholder="Email address"
						value={email}
						onChange={(e) => setEmail(e.target.value)}
						disabled={submitting}
						required
						className="h-10 rounded-lg border-border/80 bg-muted/20 text-sm dark:bg-muted/15"
					/>

					{submitError ? (
						<p className="rounded-md bg-destructive/10 px-2 py-1.5 text-xs text-destructive">
							{submitError}
						</p>
					) : null}

					<div className="mt-auto flex flex-col gap-2 pt-1 sm:flex-row sm:items-center sm:justify-between">
						<p className="min-h-[1.25rem] text-xs text-muted-foreground">
							{loading && !lastPostDate && !error ? "Loading…" : null}
							{!loading && error ? (
								<span className="text-destructive/90">{error}</span>
							) : null}
							{!loading && !error && formattedLastPublished ? (
								<span className="inline-flex items-center gap-1.5">
									<IconClock className="size-3.5 shrink-0" />
									Last published {formattedLastPublished}
								</span>
							) : null}
						</p>
						<Button
							type="submit"
							disabled={submitting || !email.trim()}
							size="sm"
							className="w-full shrink-0 rounded-full px-5 font-semibold sm:w-auto"
						>
							{submitting ? "Subscribing…" : "Subscribe"}
						</Button>
					</div>
				</form>
			</CardContent>
		</Card>
	);
}
